import { useNavigate, useSearch } from "@tanstack/react-router";
import { useReceipts } from "@/api/queries";
import type { ReceiptSummary } from "@/api/types";
import { PermittedAction } from "@/components/PermittedAction";
import type { Column } from "@/components/list/DataTable";
import { DocumentStatusBadge } from "@/components/list/StatusBadge";
import type { ListSearch } from "@/components/list/listQuery";
import { ListScreen, listQueryFor } from "@/components/list/ListScreen";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

export function ReceiptsListPage() {
  const search = useSearch({ from: "/sales/receipts" }) as ListSearch;
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useReceipts(listQueryFor(search));

  const columns: Array<Column<ReceiptSummary>> = [
    {
      key: "number",
      header: "Number",
      width: "13ch",
      numeric: true,
      render: (row) => row.number ?? "Draft",
    },
    { key: "customer", header: "Customer", render: (row) => row.customerName },
    { key: "date", header: "Date", width: "12ch", render: (row) => formatDate(row.date) },
    { key: "method", header: "Method", width: "14ch", render: (row) => row.method },
    {
      key: "applied",
      header: "Applied to",
      // "Advance" is not an invoice number — money received before there is anything to settle,
      // which is held against the customer rather than lost.
      render: (row) =>
        row.appliedTo === "Advance" ? (
          <span className="text-fg-muted">Held as advance</span>
        ) : (
          row.appliedTo
        ),
    },
    {
      key: "status",
      header: "Status",
      width: "14ch",
      render: (row) => <DocumentStatusBadge status={row.status} />,
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.amount),
    },
  ];

  return (
    <ListScreen
      route="/sales/receipts"
      title="Receipts"
      subtitle={data ? `${data.total} this fiscal year` : "Loading receipts"}
      actions={
        <PermittedAction
          variant="secondary"
          leftIcon="Plus"
          permission="sales.receipts.create"
          onClick={() => void navigate({ to: "/sales/receipts/new" })}
        >
          Record receipt
        </PermittedAction>
      }
      columns={columns}
      rows={data?.items ?? []}
      total={data?.total ?? 0}
      hasNext={data?.next != null}
      loading={isPending}
      error={error}
      onRetry={() => void refetch()}
      rowKey={(row) => row.id}
      rowLabel={(row) =>
        `Receipt ${row.number ?? "draft"}, ${row.customerName}, ${formatAmount(row.amount)}`
      }
      onRowClick={(row) => void navigate({ to: "/sales/receipts/$id", params: { id: row.id } })}
      searchPlaceholder="Search number, customer or invoice"
      empty={{
        icon: "Receipt",
        title: "No receipts yet",
        description:
          "Recording a receipt settles one or more invoices. Anything unapplied is held as a customer advance.",
      }}
      filteredEmpty={{
        icon: "Search",
        title: "No receipts match these filters",
        description: "Try a wider date range, or clear the filters to see everything again.",
      }}
    />
  );
}
